import React, { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { gsap } from "gsap";
import ContactBanner from "../../assets/img/website-images/contact-banner.png";

/* ─── Variants ─────────────────────────────────────────────────── */
const fadeDown = {
  hidden: { opacity: 0, y: -40 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.75, ease: [0.22, 1, 0.36, 1] },
  },
};

const formWrapVariants = {
  hidden: { opacity: 0, y: 50 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.15 },
  },
};

const fieldStagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.09, delayChildren: 0.3 } },
};

const fieldVariant = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: "easeOut" },
  },
};

const btnVariant = {
  hidden: { opacity: 0, scale: 0.85 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { duration: 0.5, ease: [0.34, 1.56, 0.64, 1], delay: 0.65 },
  },
};

/* ─── Main Component ───────────────────────────────────────────── */
function Search() {
  /* Main content box */
  const { ref: boxRef, inView: boxInView } = useInView({
    threshold: 0.2,
    triggerOnce: true,
  });

  /* GSAP: floating bg decorations */
  const bg10Ref = useRef(null);
  const bg08Ref = useRef(null);

  useEffect(() => {
    if (bg10Ref.current) {
      gsap.to(bg10Ref.current, {
        y: -20,
        duration: 4,
        ease: "sine.inOut",
        repeat: -1,
        yoyo: true,
      });
    }
    if (bg08Ref.current) {
      gsap.to(bg08Ref.current, {
        y: 18,
        duration: 5.2,
        ease: "sine.inOut",
        repeat: -1,
        yoyo: true,
        delay: 1,
      });
    }
  }, []);

  /* GSAP: card box glow on entry */
  const cardRef = useRef(null);
  useEffect(() => {
    if (boxInView && cardRef.current) {
      gsap.fromTo(
        cardRef.current,
        { boxShadow: "0 0 0px rgba(30,90,168,0)" },
        {
          boxShadow: "0 12px 50px rgba(30,90,168,0.18)",
          duration: 1.2,
          ease: "power2.out",
          delay: 0.4,
        },
      );
    }
  }, [boxInView]);

  /* GSAP: submit button magnetic hover */
  const submitRef = useRef(null);
  const handleBtnEnter = () =>
    gsap.to(submitRef.current, {
      scale: 1.06,
      duration: 0.3,
      ease: "power2.out",
    });
  const handleBtnLeave = () =>
    gsap.to(submitRef.current, { scale: 1, duration: 0.3, ease: "power2.out" });

  /* GSAP: input focus highlight */
  const handleFocus = (e) =>
    gsap.to(e.target, { borderColor: "#1E5AA8", duration: 0.25 });
  const handleBlur = (e) =>
    gsap.to(e.target, { borderColor: "", duration: 0.25 });

  return (
    <section
      className="search-area pt-120 pb-120 mb-80 p-relative fix"
      style={{
        backgroundImage: `url(${ContactBanner})`,
        backgroundPosition: "center center",
        backgroundRepeat: "no-repeat",
        backgroundSize: "cover",
      }}
    >
      {/* Floating BG decorations */}
      <div className="animations-10" ref={bg10Ref}>
        <img src="/assets/img/bg/an-img-04.png" alt="an-img-01" />
      </div>
      <div className="animations-08" ref={bg08Ref}>
        <img src="/assets/img/bg/an-img-05.png" alt="contact-bg-an-01" />
      </div>

      <div className="container">
        <div className="row justify-content-center align-items-center">
          <div className="col-lg-8" ref={boxRef}>
            <div className="contact-bg" ref={cardRef}>
              {/* ── Section Title ── */}
              <motion.div
                className="section-title"
                variants={fadeDown}
                initial="hidden"
                animate={boxInView ? "visible" : "hidden"}
              >
                <h2>Enquire About Admissions</h2>

                <p>
                  Interested in joining Anjanadri Institute of Pharmacy? Fill
                  out the form below to receive information about our B.Pharm
                  program, eligibility, admission process, and campus
                  facilities. Our admissions team will contact you and guide you
                  through every step of your journey toward a successful career
                  in pharmacy.
                </p>
              </motion.div>

              {/* ── Form wrapper ── */}
              <motion.form
                action="mail.php"
                method="post"
                className="contact-form mt-30"
                variants={formWrapVariants}
                initial="hidden"
                animate={boxInView ? "visible" : "hidden"}
              >
                <motion.div
                  className="row"
                  variants={fieldStagger}
                  initial="hidden"
                  animate={boxInView ? "visible" : "hidden"}
                >
                  {/* First Name */}
                  <motion.div className="col-lg-6" variants={fieldVariant}>
                    <div className="contact-field p-relative c-name mb-20">
                      <input
                        type="text"
                        id="firstn2"
                        name="firstn"
                        placeholder="First Name"
                        required
                        onFocus={handleFocus}
                        onBlur={handleBlur}
                      />
                    </div>
                  </motion.div>

                  {/* Email */}
                  <motion.div className="col-lg-6" variants={fieldVariant}>
                    <div className="contact-field p-relative c-subject mb-20">
                      <input
                        type="text"
                        id="email3"
                        name="email"
                        placeholder="Email"
                        required
                        onFocus={handleFocus}
                        onBlur={handleBlur}
                      />
                    </div>
                  </motion.div>

                  {/* Instructor */}
                  <motion.div className="col-lg-6" variants={fieldVariant}>
                    <div className="contact-field p-relative c-option mb-20">
                      <select name="instructor" id="instructor">
                        <option value="instructore">Instructor</option>
                        <option value="hot-stone-message">
                          Hot Stone Message
                        </option>
                        <option value="facil-therophy">
                          Facil &amp; Therophy
                        </option>
                      </select>
                    </div>
                  </motion.div>

                  {/* Department */}
                  <motion.div className="col-lg-6" variants={fieldVariant}>
                    <div className="contact-field p-relative c-option mb-20">
                      <select name="department" id="department">
                        <option value="department">Department</option>
                        <option value="hot-stone-message">
                          Hot Stone Message
                        </option>
                        <option value="facil-therophy">
                          Facil &amp; Therophy
                        </option>
                      </select>
                    </div>
                  </motion.div>

                  {/* Campus */}
                  <motion.div className="col-lg-6" variants={fieldVariant}>
                    <div className="contact-field p-relative c-option mb-20">
                      <select name="campus" id="campus">
                        <option value="campus">Campus</option>
                        <option value="hot-stone-message">
                          Hot Stone Message
                        </option>
                        <option value="facil-therophy">
                          Facil &amp; Therophy
                        </option>
                      </select>
                    </div>
                  </motion.div>

                  {/* Level */}
                  <motion.div className="col-lg-6" variants={fieldVariant}>
                    <div className="contact-field p-relative c-option mb-20">
                      <select name="level" id="level">
                        <option value="level">Level</option>
                        <option value="hot-stone-message">
                          Hot Stone Message
                        </option>
                        <option value="facil-therophy">
                          Facil &amp; Therophy
                        </option>
                      </select>
                    </div>
                  </motion.div>

                  {/* Submit Button */}
                  <motion.div className="col-lg-12" variants={btnVariant}>
                    <div className="slider-btn">
                      <button
                        ref={submitRef}
                        className="btn ss-btn"
                        type="submit"
                        onMouseEnter={handleBtnEnter}
                        onMouseLeave={handleBtnLeave}
                      >
                        Search Courses Here{" "}
                        <i className="fal fa-long-arrow-right" />
                      </button>
                    </div>
                  </motion.div>
                </motion.div>
              </motion.form>
            </div>
          </div>

          <div className="col-lg-4" />
        </div>
      </div>
    </section>
  );
}

export default Search;
